import { useMutation, useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  InputSelectControl,
  SettingsFormControl,
  SettingsGroup,
  useNotifications
} from '@sequencehq/core-components'

import { BASE_INPUT_FIELD_STYLES } from '../../settings.constants'

type IntegrationsJournalPostMode = 'DRAFT' | 'POSTED'

// DRAFT creates unposted journals in Xero for review; POSTED creates them in
// their final posted state without a manual review step.
const JOURNAL_POST_MODE_OPTIONS: {
  label: string
  value: IntegrationsJournalPostMode
}[] = [
  { label: 'Draft (default)', value: 'DRAFT' },
  { label: 'Posted', value: 'POSTED' }
]

export const JournalPostModeSettings = () => {
  const { displayNotification } = useNotifications()

  const revenueRecognitionSettings = useQuery(
    dashboard20240730Client.getRevenueRecognitionSettings
  )

  const revenueRecognitionSettingsMutator = useMutation(
    dashboard20240730Client.putRevenueRecognitionSettings,
    {
      onSuccess() {
        displayNotification('Revenue recognition settings updated', {
          type: 'success'
        })
      },
      onError() {
        displayNotification('Revenue recognition settings failed to update', {
          type: 'error'
        })
      }
    }
  )

  const handleChange = (value: string) => {
    const settings = revenueRecognitionSettings.data

    if (!settings) {
      return
    }

    // The PUT is a full replacement, so the other settings must be sent
    // alongside the post mode or the server resets them to their defaults.
    revenueRecognitionSettingsMutator.mutate({
      body: {
        recognizeRevenueOnAccountingDate:
          settings.recognizeRevenueOnAccountingDate,
        allocationPartialProrationStrategy:
          settings.allocationPartialProrationStrategy,
        integrationsJournalPostMode: value as IntegrationsJournalPostMode
      }
    })
  }

  return (
    <SettingsGroup
      title="Journal posting"
      isLoading={revenueRecognitionSettings.isLoading}
      footer={
        <SettingsGroup.Footer>
          Controls how revenue journals are pushed to Xero. Draft journals are
          created unposted for review before you post them in Xero, while posted
          journals are created in their final state.
        </SettingsGroup.Footer>
      }
    >
      <SettingsFormControl
        label="Journal mode"
        htmlFor="journal-post-mode"
        showBorderBottom={false}
      >
        <InputSelectControl
          id="journal-post-mode"
          data-testid="revenueRecognition.settings.journalPostMode"
          placeholder="Select journal mode"
          triggerStyle={{ width: BASE_INPUT_FIELD_STYLES.width }}
          options={JOURNAL_POST_MODE_OPTIONS}
          initialValue={
            revenueRecognitionSettings.data?.integrationsJournalPostMode
          }
          onChange={handleChange}
        />
      </SettingsFormControl>
    </SettingsGroup>
  )
}
